/**
 * /jobs — Jobs list page.
 * Canonical archetype: ContractListSection (search + FilterBuilder + DataTable + Pagination).
 * Legacy parity: ID, Title, Location, Status, Public, Locale, Type, Level, Country, Salary.
 * Row actions: Edit (→ /jobs/:id), Publish/Unpublish, Delete (ConfirmDialog).
 * Header action: Add job (→ /jobs/new), Manage categories (→ /jobs/categories).
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Briefcase, Filter, Plus, Search, SlidersHorizontal, Tag } from 'lucide-react';
import { Container, PageHeader, Toolbar } from '@vnetwork/ui/layout';
import {
  Badge,
  Button,
  Card,
  CardBody,
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuTrigger,
  Input,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Skeleton,
} from '@vnetwork/ui/primitives';
import {
  DataTable,
  EmptyState,
  FilterBuilder,
  Pagination,
  type FilterCondition,
  type FilterField,
} from '@vnetwork/ui/data';
import { Alert, ConfirmDialog, useToast } from '@vnetwork/ui/feedback';
import { paginationView } from '@/pages/customers/pagination-summary';
import {
  useJobs,
  usePublicJob,
  useUnpublicJob,
  useDeleteJob,
  type Job,
} from '@/api/jobs';
import { buildJobColumns } from './job-columns';

/* ── Constants ──────────────────────────────────────────────────────────── */

const PAGE_SIZE = 20;

const JOB_FILTER_FIELDS: FilterField[] = [
  {
    value: 'status',
    label: 'Status',
    input: 'select',
    options: [
      { value: 'Opened', label: 'Opened' },
      { value: 'Closed', label: 'Closed' },
    ],
  },
  {
    value: 'locale',
    label: 'Locale',
    input: 'select',
    options: [
      { value: 'vi-VN', label: 'vi-VN' },
      { value: 'en-US', label: 'en-US' },
    ],
  },
];

/* ── Helpers ────────────────────────────────────────────────────────────── */

function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(id);
  }, [value, delayMs]);
  return debounced;
}

const colId = (c: { id?: string; header?: unknown }): string =>
  typeof c.id === 'string' ? c.id : String(c.header ?? '');

/* ── Component ──────────────────────────────────────────────────────────── */

export default function JobsPage() {
  const navigate = useNavigate();
  const toast = useToast();

  /* ── List state ─────────────────────────────────────────────────────────── */
  const [page, setPage] = useState(1);
  const [rawQuery, setRawQuery] = useState('');
  const query = useDebouncedValue(rawQuery, 400);
  const [filterOpen, setFilterOpen] = useState(false);
  const [conditions, setConditions] = useState<FilterCondition[]>([]);
  const [visible, setVisible] = useState<Record<string, boolean>>({});

  /* ── Confirm dialogs ─────────────────────────────────────────────────────── */
  const [deleteTarget, setDeleteTarget] = useState<Job | null>(null);
  const [publicTarget, setPublicTarget] = useState<Job | null>(null);

  /* ── Derived filter params ──────────────────────────────────────────────── */
  const statusFilter = conditions.find((c) => c.field === 'status')?.value;
  const localeFilter = conditions.find((c) => c.field === 'locale')?.value;

  /* ── Data ───────────────────────────────────────────────────────────────── */
  const { data, isLoading, isFetching, isError, refetch } = useJobs({
    page,
    page_size: PAGE_SIZE,
    q: query || undefined,
    status: statusFilter || undefined,
    locale: localeFilter || undefined,
  });

  const rows = data?.results ?? [];
  const pagination = data?.pagination;
  const pageCount = pagination?.total_pages ?? 1;
  const total = pagination?.total ?? rows.length;

  /* ── Mutations ──────────────────────────────────────────────────────────── */
  const publicMut = usePublicJob();
  const unpublicMut = useUnpublicJob();
  const deleteMut = useDeleteJob();
  const publicPending = publicMut.isPending || unpublicMut.isPending;

  /* ── Columns ────────────────────────────────────────────────────────────── */
  const allColumns = useMemo(
    () =>
      buildJobColumns({
        onEdit: (row) => navigate(`/jobs/${row.job_id}`),
        onTogglePublic: (row) => setPublicTarget(row),
        onDelete: (row) => setDeleteTarget(row),
        publicPending,
      }),
    [navigate, publicPending],
  );

  // Initialise visibility map once columns are known
  useEffect(() => {
    setVisible((prev) => {
      if (Object.keys(prev).length > 0) return prev;
      return Object.fromEntries(allColumns.map((c) => [colId(c), true]));
    });
  }, [allColumns]);

  const visibleColumns = useMemo(
    () => allColumns.filter((c) => visible[colId(c)] !== false),
    [allColumns, visible],
  );

  /* ── Event handlers ─────────────────────────────────────────────────────── */
  const onSearch = (value: string) => {
    setRawQuery(value);
    setPage(1);
  };

  const applyFilters = (next: FilterCondition[]) => {
    setConditions(next.filter((c) => c.value !== ''));
    setPage(1);
    setFilterOpen(false);
  };

  const handleTogglePublic = async () => {
    if (!publicTarget) return;
    const isPublic = publicTarget.public;
    try {
      if (isPublic) {
        await unpublicMut.mutateAsync(publicTarget.job_id);
        toast.success(`"${publicTarget.title}" unpublished.`);
      } else {
        await publicMut.mutateAsync(publicTarget.job_id);
        toast.success(`"${publicTarget.title}" published.`);
      }
    } catch {
      toast.error('Failed to update publish status.');
    } finally {
      setPublicTarget(null);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteMut.mutateAsync(deleteTarget.job_id);
      toast.success(`Job "${deleteTarget.title}" deleted.`);
    } catch {
      toast.error('Failed to delete job.');
    } finally {
      setDeleteTarget(null);
    }
  };

  /* ── Render ─────────────────────────────────────────────────────────────── */
  const appliedFieldCount = new Set(conditions.map((c) => c.field)).size;

  return (
    <Container size="full">
      <PageHeader
        title="Jobs"
        description="Manage job postings — publish, edit and track open positions."
        style={{ marginBottom: 'var(--vn-space-4)' }}
        actions={
          <div style={{ display: 'flex', gap: 'var(--vn-space-2)', flexWrap: 'wrap' }}>
            <Button
              variant="secondary"
              startIcon={<Tag size={15} />}
              onClick={() => navigate('/jobs/categories')}
            >
              Manage categories
            </Button>
            <Button startIcon={<Plus size={15} />} onClick={() => navigate('/jobs/new')}>
              Add job
            </Button>
          </div>
        }
      />

      <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
        {/* Toolbar */}
        <Toolbar
          style={{ marginBottom: 0 }}
          start={
            <Input
              value={rawQuery}
              onChange={(e) => onSearch(e.target.value)}
              placeholder="Search jobs…"
              startSlot={<Search size={15} />}
              style={{ maxWidth: 320 }}
            />
          }
          end={
            <>
              <Popover open={filterOpen} onOpenChange={setFilterOpen}>
                <PopoverTrigger asChild>
                  <Button variant="secondary" startIcon={<Filter size={15} />}>
                    Filters{' '}
                    {appliedFieldCount ? (
                      <Badge tone="primary">{appliedFieldCount}</Badge>
                    ) : null}
                  </Button>
                </PopoverTrigger>
                <PopoverContent align="start" style={{ padding: 0 }}>
                  <FilterBuilder
                    fields={JOB_FILTER_FIELDS}
                    defaultValue={conditions.length ? conditions : undefined}
                    onApply={applyFilters}
                    onClose={() => setFilterOpen(false)}
                  />
                </PopoverContent>
              </Popover>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="secondary"
                    startIcon={<SlidersHorizontal size={15} />}
                  >
                    Display options
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuLabel>Edit columns</DropdownMenuLabel>
                  {allColumns.map((c) => {
                    const id = colId(c);
                    return (
                      <DropdownMenuCheckboxItem
                        key={id}
                        checked={visible[id] !== false}
                        onCheckedChange={(v) =>
                          setVisible((prev) => ({ ...prev, [id]: v === true }))
                        }
                        onSelect={(e) => e.preventDefault()}
                      >
                        {typeof c.header === 'string' ? c.header : id}
                      </DropdownMenuCheckboxItem>
                    );
                  })}
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          }
        />

        {/* Table */}
        {isError ? (
          <Alert
            tone="danger"
            title="Failed to load jobs"
            actions={
              <Button size="sm" variant="ghost" onClick={() => refetch()}>
                Retry
              </Button>
            }
          />
        ) : isLoading ? (
          <Card flat>
            <CardBody style={{ display: 'grid', gap: 'var(--vn-space-2)' }}>
              {Array.from({ length: 8 }).map((_, i) => (
                <Skeleton key={i} style={{ height: 24 }} />
              ))}
            </CardBody>
          </Card>
        ) : (
          <DataTable
            columns={visibleColumns}
            data={rows}
            getRowId={(r) => String(r.job_id)}
            onRowClick={(r) => navigate(`/jobs/${r.job_id}`)}
            emptyState={
              <EmptyState
                icon={<Briefcase size={24} />}
                title="No jobs found"
                description="Try adjusting your search or filters."
              />
            }
            footer={(() => {
              const view = paginationView(page, PAGE_SIZE, rows.length, total, pageCount);
              return (
                <Pagination
                  page={page}
                  pageCount={view.pageCount}
                  onPageChange={setPage}
                  summary={`${view.summary}${isFetching ? ' …' : ''}`}
                />
              );
            })()}
          />
        )}
      </div>

      {/* Publish/Unpublish confirm */}
      <ConfirmDialog
        open={!!publicTarget}
        onOpenChange={(o) => { if (!o) setPublicTarget(null); }}
        title={publicTarget?.public ? 'Unpublish job?' : 'Publish job?'}
        confirmLabel={publicTarget?.public ? 'Unpublish' : 'Publish'}
        loading={publicPending}
        onConfirm={handleTogglePublic}
      >
        {publicTarget?.public
          ? `"${publicTarget?.title}" will be hidden from the public listing.`
          : `"${publicTarget?.title}" will be visible in the public job listing.`}
      </ConfirmDialog>

      {/* Delete confirm */}
      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(o) => { if (!o) setDeleteTarget(null); }}
        title="Delete job?"
        confirmLabel="Delete"
        destructive
        loading={deleteMut.isPending}
        onConfirm={handleDelete}
      >
        {`Delete "${deleteTarget?.title}"? This action cannot be undone.`}
      </ConfirmDialog>
    </Container>
  );
}
